import Link from "next/link";
import { BrandLogo } from "@/components/brand-logo";
import { faqPath } from "@/lib/faq-data";
import { MODULE_GROUPS, moduleSlug, modulePath } from "@/lib/modules-data";
import {
  brandPath,
  formatCopyright,
  getDictionary,
  localePath,
  pricingPath,
  sectionPagePath,
  type Locale,
} from "@/lib/i18n";

export function SiteFooter({ locale }: { locale: Locale }) {
  const d = getDictionary(locale);
  const year = new Date().getFullYear();
  // Tüm modüller — her sayfanın HTML'inde taranabilir link ağı (SEO crawl yolları).
  const allModules = MODULE_GROUPS.flatMap((g) => g.modules);

  return (
    <footer className="border-t border-border py-10">
      <div className="mx-auto max-w-5xl px-6">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Link
              href={localePath(locale)}
              className="inline-block opacity-90 transition-opacity hover:opacity-100"
              aria-label={d.brand.name}
            >
              <BrandLogo />
            </Link>
            <p className="mt-2 max-w-xs text-xs font-medium text-muted">{d.footer.tagline}</p>
          </div>
          <nav
            className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium"
            aria-label={d.a11y.footerNav}
          >
            <Link
              href={sectionPagePath(locale, "about")}
              className="text-muted transition-colors hover:text-foreground"
            >
              {d.nav.about}
            </Link>
            <Link
              href={sectionPagePath(locale, "features")}
              className="text-muted transition-colors hover:text-foreground"
            >
              {d.nav.features}
            </Link>
            <Link
              href={pricingPath(locale)}
              className="text-muted transition-colors hover:text-foreground"
            >
              {d.nav.pricing}
            </Link>
            <Link
              href={sectionPagePath(locale, "references")}
              className="text-muted transition-colors hover:text-foreground"
            >
              {d.nav.references}
            </Link>
            <Link
              href={sectionPagePath(locale, "team")}
              className="text-muted transition-colors hover:text-foreground"
            >
              {d.nav.team}
            </Link>
            <Link
              href={sectionPagePath(locale, "contact")}
              className="text-muted transition-colors hover:text-foreground"
            >
              {d.nav.contact}
            </Link>
            <Link
              href={faqPath(locale)}
              className="text-muted transition-colors hover:text-foreground"
            >
              {locale === "en" ? "FAQ" : "SSS"}
            </Link>
            <Link
              href={brandPath(locale)}
              className="text-muted transition-colors hover:text-foreground"
            >
              {locale === "en" ? "Brand assets" : "Kurumsal kimlik"}
            </Link>
          </nav>
        </div>

        {/* Modül link ağı — taranan sayfalardan tüm modül sayfalarına doğrudan yol */}
        <div className="mt-10 border-t border-border pt-8">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground">
              {locale === "en" ? "Modules" : "Modüller"}
            </h2>
            <Link
              href={sectionPagePath(locale, "features")}
              className="text-xs font-medium text-[#F97316] underline-offset-4 hover:underline"
            >
              {locale === "en" ? "All modules →" : "Tüm modüller →"}
            </Link>
          </div>
          <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
            {allModules.map((m) => (
              <li key={moduleSlug(m, locale)}>
                <Link
                  href={modulePath(locale, moduleSlug(m, locale))}
                  className="text-xs font-medium text-muted transition-colors hover:text-foreground"
                >
                  {m.name[locale]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-8 border-t border-border pt-8 text-center text-xs font-medium text-muted sm:text-left">
          {formatCopyright(d.footer.copyright, year)}
        </p>
      </div>
    </footer>
  );
}
